import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const InstallAppButton = ({ className, variant = "outline", label = "Install App", testId = "install-app-btn" }) => {
  const [prompt, setPrompt] = useState(window.__pwaPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const onReady = () => setPrompt(window.__pwaPrompt);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("pwa-prompt-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("pwa-prompt-ready", onReady); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!prompt) return setHelp(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    window.__pwaPrompt = null;
    setPrompt(null);
  };

  return (
    <>
      <Button type="button" variant={variant} size="sm" onClick={install} className={cn("gap-2", className)} data-testid={testId}>
        <Download className="h-4 w-4" /> {label}
      </Button>
      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-sm" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Install Nex Fresh</DialogTitle>
            <DialogDescription>Add the app to your home screen. It opens full-screen, just like a regular app.</DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3 text-sm" data-testid="install-help-ios">
              <li className="flex items-center gap-2"><Share className="h-4 w-4 text-[#0F382C]" /> In Safari, tap the <b>Share</b> button</li>
              <li className="flex items-center gap-2"><PlusSquare className="h-4 w-4 text-[#0F382C]" /> Choose <b>Add to Home Screen</b></li>
              <li>Tap <b>Add</b>. The Nex Fresh icon will show up on your home screen.</li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm" data-testid="install-help-android">
              <li className="flex items-center gap-2"><MoreVertical className="h-4 w-4 text-[#0F382C]" /> In Chrome, tap the <b>⋮ menu</b> (top right)</li>
              <li className="flex items-center gap-2"><Download className="h-4 w-4 text-[#0F382C]" /> Tap <b>Install app</b> or <b>Add to Home screen</b></li>
              <li>Confirm. The Nex Fresh icon will show up on your home screen.</li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
